'use client';

/**
 * useCommandCenterTool Hook
 *
 * Allows any tool to optionally persist results to the Command Center.
 * Tools work standalone by default - when Command Center is enabled and
 * user has a workspace selected, results can be saved.
 *
 * Usage:
 * ```tsx
 * const { enabled, saveResult, linkEntities } = useCommandCenterTool({
 *   toolId: 'soc2-readiness',
 *   toolCategory: 'compliance'
 * });
 *
 * // When assessment is complete:
 * if (enabled) {
 *   await saveResult({
 *     name: 'SOC2 Readiness Assessment',
 *     score_overall: 75,
 *     scores: { security: 80, availability: 70 },
 *     answers: formAnswers,
 *     gaps: identifiedGaps,
 *     recommendations: suggestedActions
 *   });
 * }
 * ```
 */

import { useState, useCallback } from 'react';
import { useCommandCenterReady } from './CommandCenterContext';
import type { Entity } from './types';

export type ToolCategory =
  | 'compliance'
  | 'maturity'
  | 'calculator'
  | 'planner'
  | 'analyzer'
  | 'generator';

export interface AssessmentResult {
  name: string;
  score_overall?: number;
  scores?: Record<string, number>;
  answers?: Record<string, unknown>;
  gaps?: Array<{
    id: string;
    title: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    description?: string;
    recommendation?: string;
  }>;
  recommendations?: Array<{
    id: string;
    title: string;
    priority: 'high' | 'medium' | 'low';
    description?: string;
    effort?: string;
  }>;
  metadata?: Record<string, unknown>;
}

export interface SavedAssessment {
  id: string;
  workspace_id: string;
  tool_id: string;
  tool_category: ToolCategory;
  name: string;
  score_overall: number | null;
  scores: Record<string, number> | null;
  answers: Record<string, unknown> | null;
  gaps: AssessmentResult['gaps'] | null;
  recommendations: AssessmentResult['recommendations'] | null;
  metadata: Record<string, unknown> | null;
  status: 'draft' | 'completed';
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface UseCommandCenterToolOptions {
  toolId: string;
  toolCategory: ToolCategory;
}

interface UseCommandCenterToolReturn {
  /** Whether Command Center integration is available */
  enabled: boolean;
  /** Whether currently saving */
  saving: boolean;
  /** Last error message */
  error: string | null;
  /** The current/last saved assessment */
  assessment: SavedAssessment | null;
  /** Save assessment result to Command Center */
  saveResult: (result: AssessmentResult) => Promise<SavedAssessment | null>;
  /** Link entities to the current assessment */
  linkEntities: (entityIds: string[], role?: string) => Promise<boolean>;
  /** Get entities that can be linked */
  getEntities: (type?: string) => Promise<Entity[]>;
  /** Load previous assessment for this tool */
  loadPreviousAssessment: () => Promise<SavedAssessment | null>;
}

export function useCommandCenterTool({
  toolId,
  toolCategory,
}: UseCommandCenterToolOptions): UseCommandCenterToolReturn {
  // Use the optional hook that doesn't throw when provider is missing
  const { available, workspaceId } = useCommandCenterReady();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<SavedAssessment | null>(null);

  // Only enabled if provider is available and workspace is selected
  const enabled = available && !!workspaceId;

  /**
   * Save assessment result to Command Center
   */
  const saveResult = useCallback(async (result: AssessmentResult): Promise<SavedAssessment | null> => {
    if (!workspaceId) {
      setError('No workspace selected');
      return null;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch('/api/secure/command-center/assessments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          tool_id: toolId,
          tool_category: toolCategory,
          name: result.name,
          score_overall: result.score_overall ?? null,
          scores: result.scores ?? null,
          answers: result.answers ?? null,
          gaps: result.gaps ?? null,
          recommendations: result.recommendations ?? null,
          metadata: result.metadata ?? null,
          status: 'completed',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save assessment');
      }

      const saved = await response.json() as SavedAssessment;
      setAssessment(saved);
      return saved;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save assessment';
      setError(message);
      console.error('Error saving assessment:', err);
      return null;
    } finally {
      setSaving(false);
    }
  }, [workspaceId, toolId, toolCategory]);

  /**
   * Link entities to the current assessment
   */
  const linkEntities = useCallback(async (entityIds: string[], role = 'subject'): Promise<boolean> => {
    if (!assessment?.id) {
      setError('No assessment to link entities to');
      return false;
    }

    try {
      const response = await fetch(`/api/secure/command-center/assessments/${assessment.id}/entities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_ids: entityIds,
          role,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to link entities');
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link entities';
      setError(message);
      console.error('Error linking entities:', err);
      return false;
    }
  }, [assessment?.id]);

  /**
   * Get entities that can be linked to assessments
   */
  const getEntities = useCallback(async (type?: string): Promise<Entity[]> => {
    if (!workspaceId) {
      return [];
    }

    try {
      const params = new URLSearchParams({ workspace_id: workspaceId });
      if (type) {
        params.set('entity_type', type);
      }

      const response = await fetch(`/api/secure/command-center/entities?${params}`);
      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.entities || [];
    } catch (err) {
      console.error('Error fetching entities:', err);
      return [];
    }
  }, [workspaceId]);

  /**
   * Load the most recent assessment for this tool
   */
  const loadPreviousAssessment = useCallback(async (): Promise<SavedAssessment | null> => {
    if (!workspaceId) {
      return null;
    }

    try {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        tool_id: toolId,
        limit: '1',
      });

      const response = await fetch(`/api/secure/command-center/assessments?${params}`);
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const prev = data.assessments?.[0] || null;
      if (prev) {
        setAssessment(prev);
      }
      return prev;
    } catch (err) {
      console.error('Error loading previous assessment:', err);
      return null;
    }
  }, [workspaceId, toolId]);

  return {
    enabled,
    saving,
    error,
    assessment,
    saveResult,
    linkEntities,
    getEntities,
    loadPreviousAssessment,
  };
}
